import { ed25519 } from "@secondlayer/shared";
import { buildQuery, resolveApiKey, resolveBaseUrl } from "../base.ts";
import type { IndexEvent } from "../index-api/client.ts";
import type { ConsumerSink } from "../sinks/types.ts";
import {
	type StreamsEventsFetcher,
	consumeStreamsEvents,
	iterateStreamsBatches,
	streamStreamsEvents,
} from "./consumer.ts";
import { Cursor } from "./cursor.ts";
import { decode } from "./decode.ts";
import { createStreamsDumps } from "./dumps.ts";
import {
	StreamsServerError,
	StreamsSignatureError,
	ValidationError,
	mapStreamsError,
} from "./errors.ts";
import { subscribeStreamsEvents } from "./subscribe.ts";
import type {
	FetchLike,
	StreamsCanonicalBlock,
	StreamsClient,
	StreamsConsumeParams,
	StreamsEvent,
	StreamsEventType,
	StreamsEventsConsumeParams,
	StreamsEventsConsumeResult,
	StreamsEventsEnvelope,
	StreamsEventsListEnvelope,
	StreamsEventsListParams,
	StreamsEventsReplayParams,
	StreamsEventsStreamParams,
	StreamsEventsSubscribeParams,
	StreamsFilterMap,
	StreamsFilterValue,
	StreamsReorgsListEnvelope,
	StreamsReorgsListParams,
	StreamsTip,
} from "./types.ts";

/** Parse a `<block>:<index>` cursor; null sorts before genesis. */
function cursorTuple(cursor: string | null): [number, number] {
	if (!cursor) return [-1, -1];
	const { blockHeight, eventIndex } = Cursor.parse(cursor);
	return [blockHeight, eventIndex];
}

/** The greater of two cursors (later in the stream). */
function maxCursor(a: string | null, b: string | null): string | null {
	const [ah, ai] = cursorTuple(a);
	const [bh, bi] = cursorTuple(b);
	return ah > bh || (ah === bh && ai >= bi) ? a : b;
}

/**
 * The narrowing set every Streams read forwards verbatim to the consumer
 * loops. Kept in one place because each field is optional: a forgotten one is
 * a type-checkable object that silently widens the stream toward the full
 * firehose — which is billed per row, and has happened. `events.replay()`
 * deliberately forwards a subset and stays explicit.
 */
function streamsFilters(params: {
	types?: readonly StreamsEventType[];
	notTypes?: readonly StreamsEventType[];
	contractId?: StreamsFilterValue;
	sender?: StreamsFilterValue;
	recipient?: StreamsFilterValue;
	assetIdentifier?: string;
	filters?: StreamsFilterMap;
}) {
	return {
		types: params.types,
		notTypes: params.notTypes,
		contractId: params.contractId,
		sender: params.sender,
		recipient: params.recipient,
		assetIdentifier: params.assetIdentifier,
		filters: params.filters,
	};
}

export type CreateStreamsClientOptions = {
	apiKey?: string;
	baseUrl?: string;
	/** Deploy origin label sent as `x-sl-origin` (telemetry). Defaults to `cli`. */
	origin?: "cli" | "mcp" | "session";
	fetchImpl?: FetchLike;
	/**
	 * Public base URL for bulk parquet dumps (the R2/CDN bucket root). Required
	 * to use `client.dumps`. See `GET /public/streams/dumps/manifest`.
	 */
	dumpsBaseUrl?: string;
	/**
	 * Verify the ed25519 `X-Signature` on every REST response and per-frame SSE
	 * signature. Three states:
	 * - **default (omitted)** — *lenient*: verify when the server signs (the
	 *   hosted API signs every response), and pass through when no signature is
	 *   present (e.g. a self-hosted instance with no `STREAMS_SIGNING_PRIVATE_KEY`).
	 *   So verification is on by default against the hosted API without breaking
	 *   unsigned self-host deployments. An *invalid* signature always throws.
	 * - **`true`** (or `{ publicKey }` to pin a known PEM) — *strict*: a missing
	 *   OR invalid signature throws `StreamsSignatureError`. Use this when you
	 *   require a portable, non-repudiable attestation and won't accept unsigned
	 *   data (it also closes the lenient mode's strip-the-header downgrade).
	 * - **`false`** — off.
	 *
	 * The key is fetched once from `/public/streams/signing-key` (cached; a
	 * rotated `X-Signature-KeyId` triggers one refresh) unless a PEM is pinned.
	 */
	verify?: boolean | { publicKey: string };
	/**
	 * Verify the bulk dumps manifest's ed25519 signature in `client.dumps.list()`
	 * before trusting any file sha256 (default ON). Uses the same key source as
	 * `verify` (fetches `/public/streams/signing-key`, or a pinned PEM). Pass
	 * `false` to opt out. A missing or invalid signature throws
	 * `StreamsSignatureError`.
	 */
	verifyDumpsManifest?: boolean;
};

function normalizeBaseUrl(baseUrl: string): string {
	return baseUrl.replace(/\/+$/, "");
}

/** Loopback hosts, where plain http carries no more exposure than the box
 *  itself. Anywhere else, http means a network hop anyone on the path can
 *  rewrite, key endpoint included. */
function isLoopbackUrl(baseUrl: string): boolean {
	try {
		const { hostname } = new URL(baseUrl);
		return (
			hostname === "localhost" ||
			hostname === "127.0.0.1" ||
			hostname === "::1" ||
			hostname === "[::1]" ||
			hostname.startsWith("127.")
		);
	} catch {
		return false;
	}
}

export function createStreamsClient(
	options: CreateStreamsClientOptions,
): StreamsClient {
	const baseUrl = normalizeBaseUrl(resolveBaseUrl(options.baseUrl));
	// Same credential precedence as every other client (explicit option, then
	// INSTANCE_TOKEN, then SL_API_KEY), so the same code authenticates the same
	// way whether it enters through `new SecondLayer()` or here.
	const apiKey = resolveApiKey(options.apiKey);
	const origin = options.origin ?? "cli";
	const authHeaders = (): Record<string, string> => ({
		"x-sl-origin": origin,
		...(apiKey ? { Authorization: `Bearer ${apiKey}` } : {}),
	});
	const fetchImpl = options.fetchImpl ?? ((input, init) => fetch(input, init));
	const verify = options.verify;
	// On by default, but lenient: the hosted API signs every response, while a
	// self-hosted instance without STREAMS_SIGNING_PRIVATE_KEY serves none.
	// Lenient verifies when a signature is present and passes through when it is
	// absent — so the default neither skips hosted verification nor breaks OSS.
	// `verify: true` / `{ publicKey }` is strict (missing signature throws);
	// `verify: false` is off. An invalid signature always throws.
	const verifyMode: "off" | "lenient" | "strict" =
		verify === false ? "off" : verify === undefined ? "lenient" : "strict";
	// Strict mode promises the response came from the holder of a known key.
	// A key fetched over plain http from a host off the box is whatever the
	// path served, so the promise only holds with a pinned PEM there.
	if (
		verify === true &&
		baseUrl.startsWith("http:") &&
		!isLoopbackUrl(baseUrl)
	) {
		throw new ValidationError(
			`verify: true over plain http to ${baseUrl} fetches the signing key from the same unprotected path it verifies. Pin the key with verify: { publicKey } (see GET /public/streams/signing-key), or use https.`,
			400,
		);
	}

	// Lazily resolve and cache the verification key alongside its id, so a
	// rotation (signalled by a changed `X-Signature-KeyId`) can be detected.
	type VerificationKey = {
		keyId: string;
		publicKeyPem: string;
		publicKey: ReturnType<typeof ed25519.loadEd25519PublicKey>;
	};
	// Only a successful fetch stays cached. A 5xx or a dropped socket on the
	// key endpoint used to park a rejected promise here for the life of the
	// client; now it surfaces as a retryable error and the next call refetches.
	let keyPromise: Promise<VerificationKey> | null = null;
	function loadKey(): Promise<VerificationKey> {
		if (keyPromise) return keyPromise;
		const pending = (async () => {
			if (typeof verify === "object") {
				return {
					keyId: ed25519.ed25519KeyId(verify.publicKey),
					publicKeyPem: verify.publicKey,
					publicKey: ed25519.loadEd25519PublicKey(verify.publicKey),
				};
			}
			let res: Response;
			try {
				res = await fetchImpl(`${baseUrl}/public/streams/signing-key`);
			} catch {
				throw new StreamsServerError(
					`Could not reach the signing key at ${baseUrl}/public/streams/signing-key. Retryable.`,
					0,
					undefined,
					"SIGNING_KEY_UNAVAILABLE",
				);
			}
			if (!res.ok) {
				if (res.status >= 500 || res.status === 429) {
					throw new StreamsServerError(
						`Could not fetch signing key (${res.status}). Retryable.`,
						res.status,
						undefined,
						"SIGNING_KEY_UNAVAILABLE",
					);
				}
				throw new StreamsSignatureError(
					`Could not fetch signing key (${res.status}).`,
				);
			}
			const body = (await res.json()) as {
				public_key_pem?: string;
				key_id?: string;
			};
			if (!body.public_key_pem) {
				throw new StreamsSignatureError("Signing key response missing key.");
			}
			return {
				keyId: body.key_id ?? ed25519.ed25519KeyId(body.public_key_pem),
				publicKeyPem: body.public_key_pem,
				publicKey: ed25519.loadEd25519PublicKey(body.public_key_pem),
			};
		})();
		keyPromise = pending;
		pending.catch(() => {
			if (keyPromise === pending) keyPromise = null;
		});
		return pending;
	}
	/** Resolve the key a response claims to be signed with. A fetched key
	 *  refreshes once on an unknown id; a pinned key never changes, so an
	 *  unknown id is a failure either way. Shared by REST reads and SSE frames. */
	async function loadKeyFor(
		responseKeyId: string | null | undefined,
	): Promise<VerificationKey> {
		let key = await loadKey();
		if (!responseKeyId || responseKeyId === key.keyId) return key;
		if (typeof verify === "object") {
			throw new StreamsSignatureError(
				`Response signed with key '${responseKeyId}', expected pinned key '${key.keyId}'.`,
			);
		}
		// Refresh once. A still-mismatched id (no re-loop) means the endpoint
		// does not serve the signing key: fail closed.
		keyPromise = null;
		key = await loadKey();
		if (responseKeyId !== key.keyId) {
			throw new StreamsSignatureError(
				`Response signed with key '${responseKeyId}' not served by the signing-key endpoint.`,
			);
		}
		return key;
	}

	const dumps = createStreamsDumps({
		baseUrl: options.dumpsBaseUrl,
		fetchImpl,
		verifyManifest: options.verifyDumpsManifest ?? true,
		loadPublicKeyPem: async () => (await loadKey()).publicKeyPem,
	});

	async function request<T>(path: string): Promise<T> {
		const response = await fetchImpl(`${baseUrl}${path}`, {
			headers: authHeaders(),
		});
		if (!response.ok) await mapStreamsError(response);
		const text = await response.text();
		if (verifyMode !== "off") {
			const signature = response.headers.get("X-Signature");
			if (!signature) {
				// Strict requires a signature; lenient (default) lets an unsigned
				// response through — e.g. self-host with no signing key configured.
				if (verifyMode === "strict") {
					throw new StreamsSignatureError("Response is missing X-Signature.");
				}
			} else {
				const key = await loadKeyFor(response.headers.get("X-Signature-KeyId"));
				// A signature is present, so verify it regardless of strict/lenient —
				// an invalid signature always fails closed.
				if (!ed25519.verifyEd25519(text, signature, key.publicKey)) {
					throw new StreamsSignatureError();
				}
			}
		}
		return JSON.parse(text) as T;
	}

	// StreamsEventsFetchParams is a strict subset of StreamsEventsListParams, so
	// this is checked by the compiler rather than by a hand-copied destructure.
	const fetchEvents: StreamsEventsFetcher = listEvents;

	function listReorgs(params: StreamsReorgsListParams) {
		return request<StreamsReorgsListEnvelope>(
			`/v1/streams/reorgs${buildQuery({
				since: params.since,
				limit: params.limit,
			})}`,
		);
	}
	// Idle-tip reorg detection for the consume loop (one request per empty
	// page); the public `reorgs.list` is the same call.
	const fetchReorgs = (params: { since: string }) => listReorgs(params);

	function encodeFilters(filters: StreamsFilterMap | undefined) {
		return filters ? JSON.stringify(filters) : undefined;
	}

	async function listEvents(
		params: StreamsEventsListParams = {},
	): Promise<StreamsEventsEnvelope> {
		return request<StreamsEventsEnvelope>(
			`/v1/streams/events${buildQuery({
				cursor: params.cursor,
				from_height: params.fromHeight,
				to_height: params.toHeight,
				limit: params.limit,
				contract_id: params.contractId,
				sender: params.sender,
				recipient: params.recipient,
				asset_identifier: params.assetIdentifier,
				types: params.types,
				not_types: params.notTypes,
				filters: encodeFilters(params.filters),
			})}`,
		);
	}

	/**
	 * One signature, two shapes: a flat filter with `onBatch`, or a labelled
	 * `filters` map with a handler per label (or both). Deliberately NOT
	 * overloaded — an overloaded interface member strips contextual typing from
	 * every stub and alternate implementation of `StreamsClient`.
	 */
	function consumeEvents<
		const F extends StreamsFilterMap = Record<never, never>,
		TTx = never,
		D extends boolean = false,
	>(
		// Redundant `sink` member: direct TTx inference site (inference
		// does not traverse the params alias).
		params: StreamsEventsConsumeParams<TTx, D, F> & {
			sink?: ConsumerSink<TTx>;
		},
	): Promise<StreamsEventsConsumeResult> {
		const labels = params.filters ? Object.keys(params.filters) : [];
		// The "at least one handler" rule is conditional on `filters`, so it
		// can't live in the type without making `F` uninferrable. Fail loudly
		// instead of consuming pages into nothing.
		if (labels.length === 0 && !params.onBatch) {
			throw new ValidationError(
				"consume requires onBatch, or filters + on handlers.",
				400,
			);
		}
		if (labels.length > 0 && !params.on && !params.onBatch) {
			throw new ValidationError(
				"consume with filters requires on handlers (or onBatch).",
				400,
			);
		}
		const dispatch = params.on as
			| Record<
					string,
					(
						events: StreamsEvent[] | IndexEvent[],
						ctx: Parameters<
							NonNullable<StreamsEventsConsumeParams<TTx>["onBatch"]>
						>[2],
					) => void | Promise<void>
			  >
			| undefined;

		// With `decoded: true`, events arrive as the flat Index-shaped rows —
		// decoding never appears in the handler.
		const project = (events: StreamsEvent[]): StreamsEvent[] | IndexEvent[] =>
			params.decoded ? events.map(decode) : events;

		const onBatch: StreamsEventsConsumeParams<TTx>["onBatch"] = async (
			events,
			envelope,
			ctx,
		) => {
			// Label dispatch reads the wire `matched` array, so it has to run
			// BEFORE decode — the decoded row shape does not carry it.
			for (const label of labels) {
				const forLabel = events.filter((event) =>
					event.matched?.includes(label),
				);
				if (forLabel.length === 0) continue;
				await dispatch?.[label]?.(project(forLabel), ctx);
			}
			// The `decoded` flag is a runtime value here, so `project`'s return
			// can't be narrowed to the D-conditional the declared handler wants.
			const userOnBatch = params.onBatch as StreamsEventsConsumeParams<
				TTx,
				boolean
			>["onBatch"];
			// `onBatch`'s several legal return shapes collapse to one here: the
			// consume loop only ever reads a cursor string or nothing.
			return (await userOnBatch?.(project(events), envelope, ctx)) as
				| string
				| null
				| undefined;
		};

		return consumeStreamsEvents<TTx>({
			fromCursor: params.fromCursor,
			sink: params.sink,
			onProgress: params.onProgress,
			mode: params.mode,
			finalizedOnly: params.finalizedOnly,
			...streamsFilters(params),
			batchSize: params.batchSize ?? 100,
			fetchEvents,
			fetchReorgs,
			onBatch,
			onReorg: params.onReorg,
			maxRollbackDepth: params.maxRollbackDepth,
			emptyBackoffMs: params.emptyBackoffMs,
			maxPages: params.maxPages,
			maxEmptyPolls: params.maxEmptyPolls,
			signal: params.signal,
			retryCount: params.retryCount,
			retryDelay: params.retryDelay,
			onError: params.onError,
		});
	}

	return {
		consume(params: StreamsConsumeParams = {}) {
			return iterateStreamsBatches({
				fromCursor: params.cursor,
				batchSize: params.batchSize ?? 100,
				intervalMs: params.intervalMs ?? 2000,
				...streamsFilters(params),
				signal: params.signal,
				fetchEvents,
			});
		},
		events: {
			list: listEvents,
			byTxId(txId: string) {
				return request<StreamsEventsListEnvelope>(
					`/v1/streams/events/${encodeURIComponent(txId)}`,
				);
			},
			consume: consumeEvents,
			stream(params: StreamsEventsStreamParams = {}) {
				return streamStreamsEvents({
					fromCursor: params.fromCursor,
					// Includes the labelled OR-groups: dropping them here silently
					// widened the stream to the FULL firehose (billed per row).
					...streamsFilters(params),
					batchSize: params.batchSize ?? 100,
					emptyBackoffMs: params.emptyBackoffMs,
					maxPages: params.maxPages,
					maxEmptyPolls: params.maxEmptyPolls,
					signal: params.signal,
					fetchEvents,
				});
			},
			subscribe(params: StreamsEventsSubscribeParams) {
				return subscribeStreamsEvents({
					baseUrl,
					headers: authHeaders(),
					fetchImpl,
					verify: verifyMode,
					loadKey: loadKeyFor,
					reconnectDelayMs: params.reconnectDelayMs,
					staleAfterMs: params.staleAfterMs,
					params,
				});
			},
			async replay(params: StreamsEventsReplayParams) {
				const fromCursor =
					params.from === "genesis" ? null : (params.from ?? null);
				const fromBlock = fromCursor ? cursorTuple(fromCursor)[0] : 0;
				const manifest = await dumps.list();

				// Hydrate finalized history from dumps, in block order. Files
				// ending at or below `from` carry nothing new; a file straddling
				// it is handed over whole, with `from` so the handler can skip
				// the rows at or below the checkpoint (file-granular delivery).
				const files = manifest.files
					.filter((file) => file.to_block >= fromBlock)
					.filter(
						(file) =>
							fromCursor === null ||
							file.max_cursor === null ||
							maxCursor(fromCursor, file.max_cursor) !== fromCursor,
					)
					.sort(
						(a, b) => a.from_block - b.from_block || a.to_block - b.to_block,
					);
				for (const file of files) {
					if (params.signal?.aborted) break;
					await params.onDumpFile(file, { from: fromCursor });
				}

				// Seam: tail live from just past the dumped coverage. Cursor input is
				// exclusive, so the first live event is strictly after the last dump.
				const seam = maxCursor(fromCursor, manifest.latest_finalized_cursor);
				return consumeStreamsEvents({
					fromCursor: seam,
					mode: params.mode ?? "tail",
					batchSize: params.batchSize ?? 100,
					// Live-tail narrowing only — the dump phase above hands over
					// whole all-type files by design.
					types: params.types,
					notTypes: params.notTypes,
					filters: params.filters,
					fetchEvents,
					onBatch: params.onBatch,
					onReorg: params.onReorg,
					emptyBackoffMs: params.emptyBackoffMs,
					maxPages: params.maxPages,
					maxEmptyPolls: params.maxEmptyPolls,
					signal: params.signal,
				});
			},
		},
		blocks: {
			events(heightOrHash: number | string) {
				return request<StreamsEventsListEnvelope>(
					`/v1/streams/blocks/${encodeURIComponent(String(heightOrHash))}/events`,
				);
			},
		},
		reorgs: { list: listReorgs },
		dumps,
		canonical(height: number) {
			return request<StreamsCanonicalBlock>(`/v1/streams/canonical/${height}`);
		},
		tip() {
			return request<StreamsTip>("/v1/streams/tip");
		},
	};
}
